import React, { useState, useEffect, startTransition } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  useMediaQuery,
  Fab
} from '@mui/material';
import { 
  Person as PersonIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Home as HomeIcon,
  CalendarMonth as AppointmentsIcon,
  Schedule as ScheduleIcon,
  LocalHospital as DoctorIcon,
} from '@mui/icons-material';
import { FaNotesMedical } from "react-icons/fa6";
import { FaHandHoldingMedical } from "react-icons/fa";

import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SPStaffBottomNavBar from '../components/serviceprovider/SPStaffBottomNavBar';
import { serviceProviderPortalAPI } from '../services/api';

const DRAWER_WIDTH = 250;

const MenuItemWithIcon = ({ icon, text, onClick }) => (
  <MenuItem onClick={onClick}>
    <ListItemIcon>
      {icon}
    </ListItemIcon>
    <ListItemText primary={text} />
  </MenuItem>
);

const SideNavItem = ({ icon, text, path, selected, onClick }) => {
  const theme = useTheme();
  
  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        selected={selected}
        onClick={onClick}
        sx={{
          minHeight: 48,
          mx: 2,
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.2s ease-in-out',
          '&::before': selected ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            borderRadius: '0 4px 4px 0',
            backgroundColor: theme.palette.primary.dark,
            boxShadow: `0 0 12px ${alpha(theme.palette.primary.dark, 0.5)}`
          } : {},
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            backdropFilter: 'blur(20px)',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.dark, 0.12),
            }
          },
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.dark, 0.06),
            transform: 'translateX(4px)',
            '& .MuiListItemIcon-root': {
              transform: 'scale(1.1)',
            }
          }
        }}
      >
        <ListItemIcon 
          sx={{ 
            minWidth: 40,
            color: selected ? theme.palette.primary.dark : alpha(theme.palette.text.primary, 0.6),
            transition: 'all 0.2s ease-in-out',
            transform: selected ? 'scale(1.1)' : 'scale(1)',
            '& svg': {
              fontSize: '1.3rem'
            }
          }}
        >
          {icon}
        </ListItemIcon>
        <ListItemText 
          primary={text} 
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: selected ? 600 : 500,
            color: selected ? theme.palette.primary.dark : theme.palette.text.primary,
            sx: {
              transition: 'all 0.2s ease-in-out',
              opacity: selected ? 1 : 0.8
            }
          }}
        />
        {selected && (
          <Box
            sx={{
              position: 'absolute',
              right: -10,
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
              animation: 'pulse 2s infinite'
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
};

// This layout is specifically for SP Staff (Doctors)
const SPStaffLayout = ({ children, title = 'Doctor Dashboard' }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Doctor-specific navigation items
  const navigationItems = [
    { path: '/sp/staff/dashboard', text: 'Dashboard', icon: <HomeIcon /> },
    { path: '/sp/staff/appointments', text: 'Appointments', icon: <AppointmentsIcon /> },
    { path: '/sp/staff/schedule', text: 'My Schedule', icon: <ScheduleIcon /> },
    { path: '/sp/staff/profile', text: 'Profile', icon: <DoctorIcon /> },
  ];
  
  // Fetch unread notification count
  const fetchUnreadCount = async () => {
    try {
      const response = await serviceProviderPortalAPI.getUnreadCount();
      let count = 0;
      if (typeof response.data === 'number') {
        count = response.data;
      } else if (response.data && typeof response.data.unread_count === 'number') {
        count = response.data.unread_count;
      } else if (response.data && typeof response.data === 'object') {
        count = Object.values(response.data)[0] || 0;
      }
      setUnreadCount(count);
    } catch (error) {
      console.error('Error fetching staff notification count:', error);
      setUnreadCount(0);
    }
  };

  // Fetch doctor information
  const fetchDoctorInfo = async () => {
    try {
      // Get doctor ID from sp_staff_user in localStorage
      const spStaffUser = JSON.parse(localStorage.getItem('sp_staff_user') || '{}');
      const doctorId = spStaffUser?.doctor_id;
      if (!doctorId) {
        console.error('Doctor ID not found in sp_staff_user');
        return;
      }

      // Fetch specific doctor information
      const response = await serviceProviderPortalAPI.getDoctor(doctorId);
      if (response.data) {
        setDoctorInfo(response.data);
      }
    } catch (error) {
      console.error('Error fetching doctor info:', error);
    }
  };
  
  useEffect(() => {
    fetchUnreadCount();
    fetchDoctorInfo();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const handleProfileOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };
  
  const handleProfileClose = () => {
    setProfileAnchorEl(null);
  };

  const navigateToProfile = () => {
    startTransition(() => {
      navigate('/sp/staff/profile');
      handleProfileClose();
    });
  };
  

  
  const handleLogout = async () => {
    try {
      await logout();
      startTransition(() => {
        navigate('/sp/login', { replace: true });
      });
    } catch (error) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      startTransition(() => {
        navigate('/sp/login', { replace: true });
      });
    }
  };

  const handleNotificationClick = () => {
    startTransition(() => {
      navigate('/sp/staff/notifications');
    });
  };

  const renderSideNav = () => (
    <Drawer
      variant="permanent"
      elevation={0}
      sx={{
        display: { xs: 'none', md: 'block' },
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: `none`,
          background: alpha(theme.palette.neutral.light, 1),
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100vh',
          zIndex: theme.zIndex.drawer
        },
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          mt: 2,
          px: 2,
          '& .MuiList-root': {
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '20%',
              right: '20%',
              height: '1px',
              background: `linear-gradient(to right, 
                transparent,
                ${alpha(theme.palette.primary.main, 0.3)},
                transparent
              )`
            }
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <FaHandHoldingMedical style={{
            fontSize: '2.5em',
            color: theme.palette.primary.main,
            marginBottom: '12px',
            transition: 'all 0.2s ease-in-out'
          }} />
          <Typography 
            variant="overline"
            sx={{
              display: 'block',
              color: alpha(theme.palette.primary.dark, 1),
              fontWeight: 900,
              letterSpacing: '1px',
              textAlign: 'center',
              marginBottom: 1,
              fontSize: '0.8rem'
            }}
          >
           Easy Health Digital
          </Typography>
        </Box>
        
        <List>
          {navigationItems.map((item) => (
            <SideNavItem
              key={item.path}
              icon={item.icon}
              text={item.text}
              path={item.path}
              selected={location.pathname === item.path}
              onClick={() => startTransition(() => navigate(item.path))}
            />
          ))}
        </List>
      </Box>

      {/* User Section at Bottom */}
    
    </Drawer>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100%' }}>
      {/* Side Navigation */}
      {isDesktop && renderSideNav()}

      {/* Main Content Area */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        ml: { md: `${DRAWER_WIDTH}px` },
        minWidth: 0
      }}>
        {/* Header */}
        <AppBar 
          position="sticky" 
          sx={{
           
            justifyContent: { xs: 'space-between', md: 'space-between',lg: 'flex-end' },
            alignItems: { xs: 'space-between', md: 'space-between',lg: 'flex-end' },
            background: alpha(theme.palette.neutral.light, 1),
            boxShadow: 'none',
            left: { md: `${DRAWER_WIDTH}px` }
          }}
        >
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.primary.dark,
                display: { xs: 'block', md: 'none' }
              }}
            >
              Easy Health Digital
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                onClick={handleNotificationClick}
                sx={{ color: theme.palette.primary.dark }}
              >
                <Badge 
                  badgeContent={unreadCount} 
                  color="error"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.7rem',
                      height: 18,
                      minWidth: 18
                    }
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              
              <IconButton color="inherit" onClick={handleProfileOpen}>
                <Avatar sx={{ bgcolor: theme.palette.primary.dark }}>
                  {(() => {
                    const spStaffUser = JSON.parse(localStorage.getItem('sp_staff_user') || '{}');
                    return doctorInfo?.name?.charAt(0) || 
                           `${spStaffUser?.first_name || ''} ${spStaffUser?.last_name || ''}`.trim().charAt(0) || 
                           'D';
                  })()}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Profile Menu */}
        <Menu
          anchorEl={profileAnchorEl}
          open={Boolean(profileAnchorEl)}
          onClose={handleProfileClose}
          PaperProps={{
            elevation: 8,
            sx: {
              minWidth: 180,
              borderRadius: 2,
              mt: 1.5,
            }
          }}
        >
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {(() => {
                const spStaffUser = JSON.parse(localStorage.getItem('sp_staff_user') || '{}');
                return doctorInfo?.name || 
                       `${spStaffUser?.first_name || ''} ${spStaffUser?.last_name || ''}`.trim() || 
                       'Doctor';
              })()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {(() => {
                const spStaffUser = JSON.parse(localStorage.getItem('sp_staff_user') || '{}');
                return spStaffUser?.email || 'No email';
              })()}
            </Typography>
          </Box>
          <Divider />
          <MenuItemWithIcon 
            icon={<PersonIcon fontSize="small" />} 
            text="Profile" 
            onClick={navigateToProfile} 
          />

          <Divider />
          <MenuItemWithIcon 
            icon={<LogoutIcon fontSize="small" />} 
            text="Logout" 
            onClick={handleLogout} 
          />
        </Menu>

        {/* Main Content */}
        <Box sx={{ 
          flex: 1, 
          overflow: 'auto', 
          pb: { xs: 7, md: 3 },
          background: theme.palette.common.white,
          position: 'relative',
          width: '100%',
          px: { xs: 1, sm: 2, md: 0 }
        }}>
          {children}
          

        </Box>
        
        {/* Bottom Navigation - Only show on mobile */}
        {!isDesktop && <SPStaffBottomNavBar pendingNotifications={unreadCount} />}
      </Box>
    </Box>
  );
};

export default SPStaffLayout; 